import { Hub } from 'aws-amplify/utils';
import {
  confirmSignIn,
  fetchAuthSession,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  type SignInOutput,
} from 'aws-amplify/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

/**
 * Cognito-backed auth. The `useAuth()` / `RequireAuth` surface is unchanged from the M1 mock,
 * so no page needed editing.
 *
 * Tokens are held by Amplify, never by this module and never copied into application state
 * (§6.3). What is kept here is only what the UI displays.
 */

export type Role = 'viewer' | 'tester' | 'approver' | 'security-admin';

export interface User {
  name: string;
  email: string;
  role: Role;
  org: string;
}

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut';

/**
 * Where Cognito is in the sign-in conversation.
 *
 * The pool requires MFA and invites arrive with a temporary password, so a first sign-in
 * walks the whole chain: password, then a permanent password, then TOTP enrolment, then a
 * code. Modelling it explicitly keeps that flow in one place instead of scattering
 * `nextStep` checks through the page.
 */
export type SignInChallenge =
  | { kind: 'none' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; secret: string; otpauthUri: string }
  | { kind: 'totpCode' };

interface AuthState {
  user: User | null;
  status: AuthStatus;
  challenge: SignInChallenge;
  /** Returns the next challenge, or 'none' when the session is established. */
  signIn: (email: string, password: string) => Promise<SignInChallenge>;
  /** Answers the outstanding challenge: a new password, or a six-digit code. */
  respond: (answer: string) => Promise<SignInChallenge>;
  signOut: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Most privileged group wins, defaulting to `viewer`.
 *
 * This drives navigation and labelling only. It is **not** an authorisation decision: the
 * backend re-checks role, org and ownership on every request (§4), because a group claim is
 * just a claim.
 */
const ROLE_PRECEDENCE: readonly Role[] = ['security-admin', 'approver', 'tester', 'viewer'];

export function roleFromGroups(groups: unknown): Role {
  const list = Array.isArray(groups)
    ? groups.filter((g): g is string => typeof g === 'string')
    : [];
  return ROLE_PRECEDENCE.find((role) => list.includes(role)) ?? 'viewer';
}

/** Builds the display user from ID token claims. Falls back to the email when no name is set. */
export function userFromClaims(claims: Record<string, unknown>): User {
  const str = (key: string): string | undefined => {
    const value = claims[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  };

  const email = str('email') ?? '';
  const given = str('given_name');
  const family = str('family_name');
  const fullName = str('name') ?? [given, family].filter(Boolean).join(' ');

  return {
    name: fullName || email || 'Unknown user',
    email,
    role: roleFromGroups(claims['cognito:groups']),
    org: str('custom:orgId') ?? '',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<SignInChallenge>({ kind: 'none' });

  /**
   * Reads the current session into display state.
   *
   * Hoisted out of the effect because the sign-in flow calls it too: once a challenge chain
   * completes, the tokens exist but nothing has read them yet.
   */
  const refresh = useCallback(async (): Promise<void> => {
    try {
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload;
      if (!claims) {
        setUser(null);
        setStatus('signedOut');
        return;
      }
      setUser(userFromClaims(claims as Record<string, unknown>));
      setStatus('signedIn');
    } catch {
      // No session, an expired refresh token, or Amplify not configured. All mean signed out.
      setUser(null);
      setStatus('signedOut');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signedOut':
        case 'tokenRefresh':
          setError(null);
          void refresh();
          break;
        case 'tokenRefresh_failure':
          // The refresh token is gone; the session cannot be recovered without signing in.
          setError('Your session expired. Please sign in again.');
          setStatus('signedOut');
          break;
        default:
          break;
      }
    });

    void refresh();
    return unsubscribe;
  }, [refresh]);

  /**
   * Translates Amplify's `nextStep` into our challenge union.
   *
   * Anything unrecognised is treated as a failure rather than as success: an unhandled step
   * means the user is not actually signed in, and silently continuing would show an empty
   * console instead of an error.
   */
  const fromNextStep = useCallback((output: SignInOutput): SignInChallenge => {
    if (output.isSignedIn) return { kind: 'none' };

    const step = output.nextStep.signInStep;
    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') return { kind: 'newPassword' };
    if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') return { kind: 'totpCode' };
    if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      const details = output.nextStep.totpSetupDetails;
      return {
        kind: 'totpSetup',
        secret: details.sharedSecret,
        otpauthUri: details.getSetupUri('Guardian AI').toString(),
      };
    }
    throw new Error(`This account needs a sign-in step the console does not support (${step}).`);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInChallenge> => {
      setError(null);
      const next = fromNextStep(await amplifySignIn({ username: email, password }));
      setChallenge(next);
      if (next.kind === 'none') await refresh();
      return next;
    },
    [fromNextStep, refresh],
  );

  const respond = useCallback(
    async (answer: string): Promise<SignInChallenge> => {
      setError(null);
      const next = fromNextStep(await confirmSignIn({ challengeResponse: answer }));
      setChallenge(next);
      if (next.kind === 'none') await refresh();
      return next;
    },
    [fromNextStep, refresh],
  );

  const signOut = useCallback(async () => {
    try {
      // global: revokes the refresh token, not just this browser's copy.
      await amplifySignOut({ global: true });
    } finally {
      setUser(null);
      setStatus('signedOut');
      setChallenge({ kind: 'none' });
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, status, challenge, signIn, respond, signOut, error }),
    [user, status, challenge, signIn, respond, signOut, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/**
 * Gate for authenticated routes.
 *
 * While the session is still being restored it renders nothing rather than redirecting —
 * otherwise every refresh would bounce a signed-in user to `/login` before Amplify has read
 * its tokens.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="auth-pending" role="status" aria-live="polite">
        Restoring your session…
      </div>
    );
  }

  if (status === 'signedOut') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
